import React from "react";
import {
  TextField,
  Button,
  MenuItem,
  Grid,
  Typography,
  Box,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton
} from "@mui/material";
import { Add, Delete } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sharedStyles } from '../styles/sharedStyles';
import { getEnums, idTypes, titles } from '../../utils/enumConfig';

const defaultDependant = () => ({
  d_idpass: '',
  d_idnum: '',
  d_firstname: '',
  d_surname: '',
  d_dob: '',
  d_gender_radio: '',
  d_rel: '',
  d_marital: '',
  d_race: '',
  d_contact: '',
  d_altcontact: '',
  d_altemail: ''
});

const Step4Dependants = ({ formData, setFormData, errors = {}, setErrors, handleNext, handleBack }) => {
  // Use formData for dependants and registerDependants
  const registerDependants = formData.registerDependants || 'No';
  const dependants = formData.dependants || [];
  
  // Get dynamic enums based on selected plan
  const enums = getEnums(formData.recommendedMedicalAidPlan);
  const { maritalStatuses, ethnicities, relationshipOptions } = enums;
  
  const handleRegisterChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      registerDependants: value,
      dependants: value === 'No' ? [] : dependants
    });
  };

  // Map UI field names to API keys for dependants
  const depMapping = {
    idType: 'd_idpass',
    idNumber: 'd_idnum',
    firstName: 'd_firstname',
    surname: 'd_surname',
    dateOfBirth: 'd_dob',
    gender: 'd_gender_radio',
    relationship: 'd_rel',
    maritalStatus: 'd_marital',
    ethnicity: 'd_race',
    contactNumber: 'd_contact',
    alternateContactNumber: 'd_altcontact',
    email: 'd_altemail',
  };
  const handleDependantChange = (idx, field, value) => {
    const apiKey = depMapping[field] || field;
    const updated = dependants.map((d, i) => i === idx ? { ...d, [apiKey]: value } : d);
    setFormData({ ...formData, dependants: updated });
    setErrors({ ...errors, [`dependant${idx+1}_${field}`]: undefined });
  };
  const handleDependantDateChange = (idx, date) => {
    const updated = dependants.map((d, i) => i === idx ? { ...d, d_dob: date ? date.toISOString().split('T')[0] : '' } : d);
    setFormData({ ...formData, dependants: updated });
    setErrors({ ...errors, [`dependant${idx+1}_dateOfBirth`]: undefined });
  };

  const addDependant = () => setFormData({ ...formData, dependants: [...dependants, defaultDependant()] });
  const removeDependant = (idx) => setFormData({ ...formData, dependants: dependants.filter((_, i) => i !== idx) });

  const onNext = (e) => {
    e.preventDefault();
    handleNext();
  };

  return (
    <Box component="form" onSubmit={onNext} autoComplete="off" sx={sharedStyles.formContainer}>
      <Typography variant="h5" sx={sharedStyles.stepTitle}>
        DETAILS OF DEPENDANTS YOU WISH TO REGISTER
      </Typography>
      <Paper sx={sharedStyles.card}>
        <Typography sx={{ mb: 2 }}>
          The following dependants of an applicant may be registered:<br/>
          - Spouse/partner.<br/>
          - For special dependants (e.g. parents, grandchild, sibling, niece, nephew, etc.), an addition of dependants form needs to be completed together with the supporting documents required as specified on the form. Acceptance of dependants will be in accordance with the rules of the Scheme.<br/>
          - Dependent own children or adopted children (of the applicant and spouse/partner).<br/>
          - Dependent stepchildren (of the applicant and spouse/partner).<br/>
          - Foster children/children in temporary safe care/children born in terms of a surrogate motherhood agreement (of the applicant and spouse/partner). Official proof of the Court/clerk of the Court/appointed social worker must be provided in terms of the set criteria determined by Bonitas – foster children and children in temporary safe care may be registered as dependants only up to the age of 21 years in terms of legislation.
        </Typography>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Register Dependants?</Typography>
        <RadioGroup row value={registerDependants} onChange={handleRegisterChange}>
          <FormControlLabel value="Yes" control={<Radio color="primary" />} label="Yes" />
          <FormControlLabel value="No" control={<Radio color="primary" />} label="No" />
        </RadioGroup>
        {registerDependants === 'Yes' && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>
              <Typography variant="h6">Dependants</Typography>
              <Button variant="contained" color="primary" startIcon={<Add />} onClick={addDependant} sx={{ minWidth: 120 }}>
                Add
              </Button>
            </Box>
            {dependants.map((dep, idx) => (
              <Paper key={idx} sx={{ p: 2, mb: 3, position: 'relative', background: '#fafbfc' }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <IconButton onClick={() => removeDependant(idx)} color="error" size="small"><Delete /></IconButton>
                </Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Dependant {idx + 1}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField select label="ID / Passport" name="d_idpass" value={dep.d_idpass || ''} onChange={e => handleDependantChange(idx, 'd_idpass', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_idpass`]} helperText={errors[`dependant${idx+1}_d_idpass`]}>
                      {idTypes.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="ID / Passport Number" name="d_idnum" value={dep.d_idnum || ''} onChange={e => handleDependantChange(idx, 'd_idnum', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_idnum`]} helperText={errors[`dependant${idx+1}_d_idnum`]} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Title" name={`${depMapping.title} || ''`} value={dep.d_title || ''} onChange={e => handleDependantChange(idx, 'd_title', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_title`]} helperText={errors[`dependant${idx+1}_d_title`]}>
                      {titles.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Surname" name={`${depMapping.surname} || ''`} value={dep.d_surname || ''} onChange={e => handleDependantChange(idx, 'd_surname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_surname`]} helperText={errors[`dependant${idx+1}_d_surname`]} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="First Name" name={`${depMapping.firstName} || ''`} value={dep.d_firstname || ''} onChange={e => handleDependantChange(idx, 'd_firstname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_firstname`]} helperText={errors[`dependant${idx+1}_d_firstname`]} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Marital Status" name={`${depMapping.maritalStatus} || ''`} value={dep.d_marital || ''} onChange={e => handleDependantChange(idx, 'd_marital', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_marital`]} helperText={errors[`dependant${idx+1}_d_marital`]}>
                      {maritalStatuses.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ mb: 1, fontWeight: 500 }}>Gender</Typography>
                    <RadioGroup row name={`${depMapping.gender} || ''`} value={dep.d_gender_radio || ''} onChange={e => handleDependantChange(idx, 'd_gender_radio', e.target.value)}>
                      <FormControlLabel value="M" control={<Radio color="primary" />} label="M" />
                      <FormControlLabel value="F" control={<Radio color="primary" />} label="F" />
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Date of Birth"
                        value={dep.d_dob ? new Date(dep.d_dob) : null}
                        onChange={date => handleDependantDateChange(idx, date)}
                        renderInput={(params) => (
                          <TextField {...params} name={`${depMapping.dateOfBirth} || ''`} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_dob`]} helperText={errors[`dependant${idx+1}_d_dob`]} />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Ethnicity" name={`${depMapping.ethnicity} || ''`} value={dep.d_race || ''} onChange={e => handleDependantChange(idx, 'd_race', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_race`]} helperText={errors[`dependant${idx+1}_d_race`]}>
                      {ethnicities.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Relationship to Main Member" name={`${depMapping.relationship} || ''`} value={dep.d_rel || ''} onChange={e => handleDependantChange(idx, 'd_rel', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_rel`]} helperText={errors[`dependant${idx+1}_d_rel`]}>
                      {relationshipOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Contact Number" name={`${depMapping.contactNumber} || ''`} value={dep.d_contact || ''} onChange={e => handleDependantChange(idx, 'd_contact', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_contact`]} helperText={errors[`dependant${idx+1}_d_contact`]} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Alternate Contact Number" name={`${depMapping.alternateContactNumber} || ''`} value={dep.d_altcontact || ''} onChange={e => handleDependantChange(idx, 'd_altcontact', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_altcontact`]} helperText={errors[`dependant${idx+1}_d_altcontact`]} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Email" name={`${depMapping.email} || ''`} value={dep.d_altemail || ''} onChange={e => handleDependantChange(idx, 'd_altemail', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`dependant${idx+1}_d_altemail`]} helperText={errors[`dependant${idx+1}_d_altemail`]} />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Step4Dependants; 